/**
 * Normalize a caught API error (from the axios client in `api.js`) into
 * form-friendly pieces. Single source of truth for reading DRF error bodies —
 * components should not re-implement `err.response.data` parsing.
 *
 * Handles the shapes DRF actually returns:
 *   - `{ detail: "..." }`             → form-level (permission / auth / not-found)
 *   - `{ non_field_errors: [...] }`   → form-level
 *   - `{ field: [msgs] | "msg" }`     → per-field (value kept raw)
 *   - `["msg", ...]` (bare array)     → form-level (list-level ValidationError)
 *   - a plain string body             → form-level
 *   - an HTML error page (Django's default 500, a proxy's 502/503 — sent
 *     as text/html, or markup without a type) → "The server had a problem
 *     (500). Try again in a moment." — never the markup itself
 *   - a transport error (no response) → `err.message`
 *
 * Nested serializers (Training's settings, set batches, plans) answer with
 * objects and lists of objects instead of strings. Those are flattened to
 * dotted paths, so an error can land on the nested input it belongs to:
 *   `{bands: [{}, {assist: ["…"]}]}`        → `"bands.1.assist": ["…"]`
 *   `{equipment: {items: ["…"]}}`          → `"equipment.items": ["…"]`
 *   `{equipment: {items: {0: ["…"]}}}`     → `"equipment.items.0": ["…"]`
 *   `{plan: {non_field_errors: ["…"]}}`    → `"plan": ["…"]` (the object's own)
 * A list's empty `{}` (an item that was fine) contributes nothing; strings
 * beside objects in a list belong to the list itself. Flat values are kept
 * exactly as before (the raw array or string).
 *
 * @returns {{ formError: string|null, fieldErrors: Record<string, string|string[]> }}
 *   `formError` is a banner-level message (null when only field errors exist);
 *   `fieldErrors` maps field name (or dotted path) → the DRF value (array or
 *   string), ready to feed a form input (consumers typically do
 *   `[].concat(value).join(" ")`).
 */
export function parseApiError(err, fallback = "Something went wrong.") {
  const data = err?.response?.data;

  if (isErrorPage(err?.response)) {
    const status = err.response.status;
    return {
      formError: `The server had a problem${Number.isInteger(status) ? ` (${status})` : ""}. Try again in a moment.`,
      fieldErrors: {},
    };
  }
  if (typeof data === "string" && data.trim()) {
    return { formError: data.trim(), fieldErrors: {} };
  }
  // A bare array is a list-level ValidationError — its strings are the message.
  if (Array.isArray(data)) {
    const text = data.filter((m) => typeof m === "string").join(", ");
    return {
      formError: text || err?.message || fallback,
      fieldErrors: {},
    };
  }
  if (!data || typeof data !== "object") {
    return { formError: err?.message || fallback, fieldErrors: {} };
  }

  const fieldErrors = {};
  let formError = null;
  for (const [key, value] of Object.entries(data)) {
    if (key === "detail" || key === "non_field_errors") {
      const text = [].concat(value).join(", ");
      formError = formError ? `${formError} — ${text}` : text;
    } else if (isNested(value)) {
      flatten(value, key, fieldErrors);
    } else {
      fieldErrors[key] = value;
    }
  }
  // An empty / unrecognized object body still deserves a banner.
  if (!formError && Object.keys(fieldErrors).length === 0) {
    formError = err?.message || fallback;
  }
  return { formError, fieldErrors };
}

/**
 * Flatten a caught API error to a single human-readable string, for components
 * that show one banner (no per-field placement).
 */
export function apiErrorMessage(err, fallback = "Something went wrong.") {
  const { formError, fieldErrors } = parseApiError(err, fallback);
  const parts = [
    formError,
    ...Object.entries(fieldErrors).map(
      ([field, value]) => `${field}: ${[].concat(value).join(", ")}`,
    ),
  ].filter(Boolean);
  return parts.length ? parts.join(" — ") : fallback;
}

/** The response's Content-Type: AxiosHeaders' `get`, or a plain object's key in any case. */
function contentType(headers) {
  if (!headers || typeof headers !== "object") return "";
  if (typeof headers.get === "function") return String(headers.get("content-type") ?? "");
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "content-type");
  return key ? String(headers[key] ?? "") : "";
}

/** A string body that is a page, not a message: sent as HTML, or markup. */
function isErrorPage(response) {
  const data = response?.data;
  if (typeof data !== "string" || !data.trim()) return false;
  return /html/i.test(contentType(response.headers)) || /^\s*</.test(data);
}

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** An object, or a list holding one: a nested serializer's error value. */
const isNested = (value) =>
  isObject(value) ||
  (Array.isArray(value) && value.some((v) => v && typeof v === "object"));

function addMessage(out, path, message) {
  out[path] = [...[].concat(out[path] ?? []), message];
}

/** Every message under `value`, filed under its dotted path in `out`. */
function flatten(value, path, out) {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (item && typeof item === "object") {
        flatten(item, `${path}.${index}`, out);
      } else if (item !== null && item !== undefined && item !== "") {
        addMessage(out, path, String(item));
      }
    });
    return;
  }
  if (isObject(value)) {
    for (const [key, inner] of Object.entries(value)) {
      // An object's own errors (a nested validate()) belong to the object.
      const child = key === "non_field_errors" ? path : `${path}.${key}`;
      flatten(inner, child, out);
    }
    return;
  }
  if (value !== "") addMessage(out, path, String(value));
}
